import { generate } from 'generate-password';
import React from 'react';

import { ADMIN_JOB_STATUS, ROLE_LIST } from '@common/constants';
import { CLIENT_FORM } from '@common/schema/flow';
import { JOB_FORM } from '@common/schema/job';
import {
  useCreateB2CClientProfileMutation,
  useCreateJobMutation,
  useCreateQuoteMutation,
} from '@controller/schema';
import {
  processBigCalendarTimeSlots,
} from '@modules/job/update/processBigCalendarTimeSlots';
import {
  savePersonalInformation,
} from '@modules/job/update/savePersonalInformation';
import { FlowDataStore, useFlowDataStore } from '@modules/store/FlowDataStore';
import { fromBase64data } from '@util/saveAndRetrieveImage';

import { ThankYouView } from './ThankYouView';

const selectorFlowData = (state: FlowDataStore) => state.flowData;
const selectorSetFlowData = (state: FlowDataStore) => state.setFlowData;

type Props = {
  payment_intent: string;
};

export const ThankYouConnector = ({ payment_intent }: Props) => {
  const { mutate } = useCreateB2CClientProfileMutation();
  const jobCreatemutation = useCreateJobMutation();
  const createQuoteMutation = useCreateQuoteMutation();

  const flowData = useFlowDataStore(selectorFlowData);
  const setFlowData = useFlowDataStore(selectorSetFlowData);

  const createClient = async (onComplete: any) => {
    if (flowData) {
      const cityId = flowData[CLIENT_FORM.CITY].id;
      const address = flowData[CLIENT_FORM.ADDRESS];
      mutate(
        {
          input: {
            errorPath: '',
            isB2B: 0,
            fullName: flowData[CLIENT_FORM.FULL_NAME],
            phoneNumber: flowData[CLIENT_FORM.PHONE_NUMBER],
            email: flowData[CLIENT_FORM.EMAIL],
            address,
            cityId,
            roles: [ROLE_LIST.CLIENT.name],
            password: generate({
              length: 10,
              numbers: true,
              excludeSimilarCharacters: true,
            }),
            stripePaymentIntentId: payment_intent,
            // emailConfirmationLink: "" // TODO: need to send email confirmation about account
          },
        },
        {
          onSuccess: async (result) => {
            const profile = result.createB2CClientProfile.profile;
            // what happens if the email is already taken?
            // we want to fetch existing profile in that case and return that profile
            if (profile) {
              let startTimeSlots: any[] = processBigCalendarTimeSlots(
                flowData[CLIENT_FORM.DATE_AND_TIME],
              );
              const images = flowData[CLIENT_FORM.IMAGES] ?? [];
              const videos = flowData[CLIENT_FORM.VIDEOS] ?? [];

              let problemCategoryId = JSON.parse(
                flowData[JOB_FORM.PROBLEM_CATEGORY_ID],
              );
              let problemSubcategoryId = JSON.parse(
                flowData[JOB_FORM.PROBLEM_SUB_CATEGORY_ID],
              );

              const finalImages = await fromBase64data(images);
              const finalVideos = await fromBase64data(videos);

              const location = flowData[JOB_FORM.LOCATION];
              const postCode = flowData[JOB_FORM.POST_CODE];
              const skillList = flowData[JOB_FORM.SKILL_LIST];
              const skillListInput =
                skillList?.map((skill: any) => +skill.id) ?? [];

              jobCreatemutation.mutate(
                {
                  input: {
                    errorPath: '',
                    publishedToApp: false,
                    address,
                    postCode: postCode ?? '',
                    lat: location?.lat ?? 1,
                    lng: location?.lng ?? 1,
                    cityId,
                    clientId: profile.id,
                    // clientCost: flowData[CLIENT_FORM.CLIENT_COST],

                    title: problemSubcategoryId.name,
                    details: flowData[CLIENT_FORM.DESCRIPTION],

                    problemCategoryId: problemCategoryId?.id ?? undefined,
                    problemSubcategoryId: problemSubcategoryId?.id ?? undefined,
                    images: finalImages,
                    videos: finalVideos,
                    startTimeSlots,
                    slotDuration: 8,
                    jobStatusId: ADMIN_JOB_STATUS.PARTIAL_JOB.id,
                    ...savePersonalInformation(flowData),
                    propertyAccess: flowData[JOB_FORM.PROPERTY_ACCESS],
                    referenceId: 'ext',
                    emailConfirmationLink: 'b2c',

                    skillList: skillListInput,
                  },
                },
                {
                  onSuccess: (result) => {
                    if (result && result.createJob?.job) {
                      createQuoteMutation.mutate({
                        input: {
                          errorPath: '',
                          labourRevenue: flowData[CLIENT_FORM.CLIENT_COST],
                          reportJobId: result.createJob.job.id,
                          isJobReport: true,
                        },
                      });
                    }
                    setFlowData(null);
                    onComplete();
                  },
                  onError: (error) => {
                    console.log(error);
                  },
                },
              );
            }
          },
          onError: (error) => {
            console.log(error);
          },
        },
      );
    }
  };
  return (
    <ThankYouView
      payment_intent={payment_intent as string}
      onSubmit={createClient}
    />
  );
};
